// src/pages/RaceSetup.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../App.css';
import './RaceSetup.css';
import { Icon } from '../components/icons';
import LiveDeviceValue from '../components/LiveDeviceValue';
import Memo from '../components/Memo';
import CircuitPicker from '../components/CircuitPicker';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? `wss://${window.location.hostname}/ws` : `ws://${window.location.hostname}:5001`);

// Ruolo speciale: il dispositivo è la vettura "bandiera gialla" (safety/marshal
// car). Il backend attiva da solo la YELLOW FLAG quando entra in pista.
const FLAG_ROLE = 'FLAG_DEVICE';
const PILOT_DEFAULT = `${API_BASE}/uploads/pilotdefault.png`;
const TEAM_DEFAULT = `${API_BASE}/uploads/teamdefault.png`;

// Icona "meno" per gli stepper (non presente nel set globale).
const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /></svg>
);

export default function RaceSetup({ onStartRace }) {
  const [circuits, setCircuits] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [teams, setTeams] = useState([]);

  const [eventType, setEventType] = useState('race');   // 'race' | 'qualifying'
  const [eventName, setEventName] = useState('');
  const [eventDetails, setEventDetails] = useState('');

  const [selectedCircuit, setSelectedCircuit] = useState(null);
  const [circuitData, setCircuitData] = useState(null);

  const [totalLaps, setTotalLaps] = useState(10);
  const [unlimitedLaps, setUnlimitedLaps] = useState(false);
  const [maxYellowFlagSpeed, setMaxYellowFlagSpeed] = useState(50);
  const [raceDurationMinutes, setRaceDurationMinutes] = useState(null); // Timer mode (null = disabled)
  const [useTimerMode, setUseTimerMode] = useState(false); // Toggle per modalità timer

  const [activeDevices, setActiveDevices] = useState({});   // presenza e stato (fix, fascia)
  const liveRef = useRef({});                                // valori vivi per dispositivo
  const shownRef = useRef({});                               // copia di activeDevices per il confronto
  const [deviceAssignments, setDeviceAssignments] = useState({});
  const [deviceAliases, setDeviceAliases] = useState({});

  const [raceInProgress, setRaceInProgress] = useState(null);

  const wsRef = useRef(null);
  const pilotsRef = useRef([]);
  const aliasesRef = useRef({});
  const assignmentsRef = useRef({});

  useEffect(() => { pilotsRef.current = pilots; }, [pilots]);
  useEffect(() => { aliasesRef.current = deviceAliases; }, [deviceAliases]);
  useEffect(() => { assignmentsRef.current = deviceAssignments; }, [deviceAssignments]);

  const [pilotQueryByMac, setPilotQueryByMac] = useState({});

  const normalize = (s = '') =>
    String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

  // Filter pilots AND teams together for assignment
  const filterPilotsAndTeams = (q) => {
    const nq = normalize(q);
    if (!nq) return [];

    // Piloti/team già assegnati a un altro device non vengono riproposti:
    // un partecipante può avere un solo dispositivo per sessione.
    const assignedIds = new Set(
      Object.values(deviceAssignments).filter(Boolean).map((id) => String(id))
    );

    // Filter pilots
    const matchedPilots = pilots
      .filter((p) => !assignedIds.has(String(p.id)))
      .filter((p) => {
        const full = normalize(`${p.name} ${p.surname} ${p.team || ''}`);
        return full.includes(nq);
      })
      .map(p => ({ ...p, type: 'pilot' }));

    // Filter teams
    const matchedTeams = teams
      .filter((t) => !assignedIds.has(String(t.id)))
      .filter((t) => {
        const full = normalize(`${t.name} ${t.description || ''}`);
        return full.includes(nq);
      })
      .map(t => ({ ...t, type: 'team' }));

    // Combine and limit
    return [...matchedPilots, ...matchedTeams].slice(0, 8);
  };

  const selectPilotForMac = (mac, p) => {
    setDeviceAssignments((prev) => ({ ...prev, [mac]: String(p.id) }));
    setPilotQueryByMac((s) => ({ ...s, [mac]: '' }));
  };

  // Assegnazione automatica: il numero stampato sul dispositivo (alias) è
  // associato in anagrafica a un team o a un pilota (campo ID dispositivo).
  // Appena il device compare, gli mettiamo accanto il suo equipaggio; l'ordine
  // di ricerca è: anagrafica per ID dispositivo, poi i vecchi alias per cognome.
  const rosterRef = useRef({});          // MAC → { deviceId, entityId, entityName, type }
  const autoAssign = useCallback((mac) => {
    const macUp = String(mac).toUpperCase();
    if (assignmentsRef.current[mac]) return;
    const hit = rosterRef.current[macUp];
    // Vettura bandiera gialla: ruolo speciale, non un concorrente in classifica.
    if (hit && hit.type === 'flag') {
      setDeviceAssignments(asn => (asn[mac] ? asn : { ...asn, [mac]: FLAG_ROLE }));
      return;
    }
    let targetId = hit && hit.entityId ? String(hit.entityId) : null;
    if (!targetId) {
      const alias = aliasesRef.current[macUp];
      if (!alias) return;
      const target = String(alias).trim();
      const matchers = [
        p => p.championship?.name === 'RaceSense Devices' && String(p.surname || '').trim() === target,
        p => String(p.surname || '').trim() === target,
        p => String(p.name || '').trim() === target,
      ];
      let pilot = null;
      for (const fn of matchers) { pilot = pilotsRef.current.find(fn); if (pilot) break; }
      if (!pilot) return;
      targetId = String(pilot.id);
    }
    // Un equipaggio su un solo dispositivo: se è già in elenco, non lo spostiamo.
    if (Object.values(assignmentsRef.current).some(v => String(v) === targetId)) return;
    setDeviceAssignments(asn => (asn[mac] ? asn : { ...asn, [mac]: targetId }));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/circuits`).then(r => r.json()).then(setCircuits).catch(console.error);
    fetch(`${API_BASE}/api/pilots`).then(r => r.json()).then(setPilots).catch(console.error);
    fetch(`${API_BASE}/api/teams`).then(r => r.json()).then(setTeams).catch(console.error);
    fetch(`${API_BASE}/api/device-aliases`)
      .then(r => r.json())
      .then(a => setDeviceAliases(a && typeof a === 'object' ? a : {}))
      .catch(() => { });
    fetch(`${API_BASE}/api/device-roster`)
      .then(r => r.json())
      .then(j => {
        const map = {};
        (j?.devices || []).forEach(d => { map[String(d.mac).toUpperCase()] = d; });
        rosterRef.current = map;
      })
      .catch(() => { });
    fetch(`${API_BASE}/api/race/state`)
      .then(r => r.json())
      .then(s => { if (s?.type === 'race_snapshot') setRaceInProgress(s); })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!selectedCircuit) return;
    fetch(`${API_BASE}/api/circuits/${selectedCircuit}`).then(r => r.json()).then(setCircuitData).catch(console.error);
  }, [selectedCircuit]);

  useEffect(() => {
    // Connessione con RICONNESSIONE AUTOMATICA: se il backend viene riavviato o
    // il proxy chiude il socket, la scoperta dei device riprende da sola senza
    // dover ricaricare la pagina di setup.
    let cancelled = false;
    let reconnectTimer = null;
    let ws = null;
    // Pacchetti GPS dei dispositivi (fino a 15 al secondo ciascuno): i valori
    // vivi (velocità, battito) restano in liveRef e li mostra LiveDeviceValue;
    // la pagina si ridisegna solo quando un dispositivo compare, sparisce o
    // cambia stato (fix GPS, fascia cardio). Ridisegnarla a ogni pacchetto
    // teneva il browser sempre occupato e il menu non cambiava più pagina.
    const live = liveRef.current;
    const flushDevices = () => {
      const now = Date.now();
      // spento o fuori copertura da più di 6 s: fuori elenco
      for (const mac of Object.keys(live)) if (now - live[mac].lastSeen > 6000) delete live[mac];
      // confronto qui, fuori da React: se nulla è cambiato non si chiede
      // nemmeno l'aggiornamento (React rieseguirebbe comunque la pagina)
      const prev = shownRef.current;
      const macs = Object.keys(live);
      let changed = Object.keys(prev).length !== macs.length;
      const next = {};
      const fresh = [];
      for (const mac of macs) {
        const d = live[mac], p = prev[mac];
        const st = { fix: d.fix, hasHr: d.hr != null, beaconId: d.beaconId };
        if (!p) { fresh.push(mac); next[mac] = st; changed = true; continue; }
        if (p.fix !== st.fix || p.hasHr !== st.hasHr || p.beaconId !== st.beaconId) { next[mac] = st; changed = true; } else next[mac] = p;
      }
      if (!changed) return;
      shownRef.current = next;
      setActiveDevices(next);
      fresh.forEach(autoAssign);
    };
    const flushTimer = setInterval(flushDevices, 500);

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data?.type === 'race_init') {
          setRaceInProgress({
            totalLaps: data.totalLaps,
            raceStatus: data.raceStatus,
            circuit: data.circuit
          });
          return;
        }
        if (data?.type === 'race_snapshot') { setRaceInProgress(data); return; }
        if (data?.type === 'race_inactive') { setRaceInProgress(null); return; }

        // Device vivo anche senza fix GPS (ai box): il server manda fix:false e
        // coordinate nulle; lo mostriamo comunque, con lo stato della fascia cardio.
        if (data?.type === 'gps_raw' && data?.data?.mac) {
          const { mac, lat, lon, speedKmh, hr, beaconId } = data.data;   // lat/lon: solo per il fix
          const hasFix = data.data.fix !== false && Number.isFinite(Number(lat)) && Number.isFinite(Number(lon)) && !(Math.abs(Number(lat)) < 0.01 && Math.abs(Number(lon)) < 0.01);
          const speed = hasFix ? Number(speedKmh || 0) : 0;
          const hrVal = (Number.isFinite(Number(hr)) && Number(hr) > 0) ? Number(hr) : null;
          const beaconVal = (Number.isFinite(Number(beaconId)) && Number(beaconId) > 0) ? Number(beaconId) : null;
          live[mac] = { lastSeen: Date.now(), speed, fix: hasFix, hr: hrVal, beaconId: beaconVal };
        }
      } catch { }
    };
    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => console.log('[RaceSetup] WS connesso');
      ws.onmessage = handleMessage;
      ws.onerror = e => console.error('[RaceSetup] WS errore', e);
      ws.onclose = () => {
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearInterval(flushTimer);
      try { ws && ws.close(); } catch { }
    };
  }, []);

  const handleStart = async () => {
    try {
      const s = await fetch(`${API_BASE}/api/race/state`).then(r => r.json());
      if (s?.type === 'race_snapshot') { setRaceInProgress(s); return; }

      if (!selectedCircuit) return alert('ATTENZIONE: seleziona un circuito!');
      const assignedDevices = Object.keys(deviceAssignments).filter(mac => deviceAssignments[mac] && deviceAssignments[mac] !== FLAG_ROLE);
      if (assignedDevices.length === 0) return alert('ATTENZIONE: assegna almeno un pilota a un device attivo!');

      const body = {
        circuitId: selectedCircuit,
        eventType,
        eventName: eventName.trim(),
        eventDetails: eventDetails.trim(),
        totalLaps: useTimerMode ? null : (unlimitedLaps ? null : totalLaps),
        maxYellowFlagSpeed: maxYellowFlagSpeed > 0 ? Number(maxYellowFlagSpeed) : null,
        raceDurationMinutes: useTimerMode && raceDurationMinutes > 0 ? Number(raceDurationMinutes) : null,
        assignments: deviceAssignments,
        pilots: [...pilots, ...teams]
      };
      const r = await fetch(`${API_BASE}/api/race/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || 'Errore avvio gara');

      onStartRace({ circuit: selectedCircuit, circuitData, totalLaps: (useTimerMode || unlimitedLaps) ? null : totalLaps, assignments: deviceAssignments, pilots: [...pilots, ...teams] });
    } catch (e) {
      console.error(e);
      alert('Errore nell\'avvio della gara: ' + e.message);
    }
  };

  const goToExistingRace = async () => {
    try {
      const cid = raceInProgress?.circuit?.id;
      let cData = circuitData;
      if (cid && (!circuitData || selectedCircuit !== cid)) {
        cData = await fetch(`${API_BASE}/api/circuits/${cid}`).then(r => r.json()).catch(() => null);
      }
      onStartRace({
        circuit: cid || selectedCircuit,
        circuitData: cData || circuitData,
        totalLaps: raceInProgress?.totalLaps ?? totalLaps,
        assignments: {},
        pilots: [...pilots, ...teams]
      });
    } catch (e) {
      console.error(e);
      onStartRace({ circuit: selectedCircuit, circuitData, totalLaps, assignments: {}, pilots: [...pilots, ...teams] });
    }
  };

  const activeMacs = Object.keys(activeDevices);
  const assignedCount = Object.values(deviceAssignments).filter(v => v && v !== FLAG_ROLE).length;
  const flagCount = Object.values(deviceAssignments).filter(v => v === FLAG_ROLE).length;

  const selectedCircuitName = circuits.find(c => c.id === selectedCircuit)?.name || '';
  const startSummary = selectedCircuit && assignedCount > 0
    ? `${assignedCount} ${assignedCount === 1 ? 'pilota' : 'piloti'}${flagCount ? ` · ${flagCount} vettura bandiera` : ''}${useTimerMode ? ` · ${raceDurationMinutes || 60} minuti` : (unlimitedLaps ? ' · Endurance' : ` × ${totalLaps} giri`)} + Formation Lap`
    : 'Configura circuito e piloti per iniziare';

  return (
    <div className="rs-page setup-page">

      {/* ═══ HEADER ═══ */}
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Sessione · Gara</span>
          <h1 className="rs-page-header__title">Configurazione <em>gara</em></h1>
          <p className="rs-page-header__subtitle">Circuito, dispositivi GPS, piloti e parametri della sessione. La gara parte con il formation lap.</p>
        </div>
        <div className="rs-page-header__actions">
          <div className="setup-start">
            <button type="button" className="rs-btn rs-btn--primary rs-btn--xl" onClick={handleStart}>
              <Icon.Play size={18} /> Start race
            </button>
            <span className="setup-start__hint">{startSummary}</span>
          </div>
        </div>
      </header>

      {/* ═══ BANNER GARA IN CORSO ═══ */}
      {raceInProgress && (
        <div className="rs-alert rs-alert--red setup-alert" role="status">
          <Icon.Zap />
          <div className="setup-alert__text">
            <strong>Gara in corso</strong>
            <span>Circuito: {raceInProgress.circuit?.name || raceInProgress.circuit?.id}</span>
          </div>
          <div className="setup-alert__actions">
            <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm" onClick={() => setRaceInProgress(null)}>Chiudi</button>
            <button type="button" className="rs-btn rs-btn--primary rs-btn--sm" onClick={goToExistingRace}><Icon.Eye size={14} /> Visualizza gara</button>
          </div>
        </div>
      )}

      {/* ═══ STAT ═══ */}
      <section className="setup-stats" aria-label="Riepilogo configurazione">
        <div className="rs-stat">
          <span className="rs-stat__label">Circuiti</span>
          <span className="rs-stat__value">{circuits.length}</span>
          <span className="rs-stat__hint rs-truncate">{selectedCircuitName ? `Selezionato: ${selectedCircuitName}` : 'Nessun circuito selezionato'}</span>
        </div>
        <div className={`rs-stat ${activeMacs.length ? 'rs-stat--lime' : ''}`}>
          <span className="rs-stat__label">Device attivi</span>
          <span className="rs-stat__value">{activeMacs.length}</span>
          <span className="rs-stat__hint">GPS in ricezione negli ultimi 6 s</span>
        </div>
        <div className={`rs-stat ${assignedCount ? 'rs-stat--red' : ''}`}>
          <span className="rs-stat__label">Piloti assegnati</span>
          <span className="rs-stat__value">{assignedCount}<small>/ {activeMacs.length} device</small></span>
          <span className="rs-stat__hint">{assignedCount ? 'Pronti per la griglia' : 'Assegna almeno un pilota'}</span>
        </div>
      </section>

      <div className="setup-stack">

        {/* ═══ TIPOLOGIA EVENTO ═══ */}
        <section className="rs-card">
          <div className="rs-card__head setup-card-head">
            <span className="rs-card__title"><Icon.Flag /> Tipologia evento</span>
            <div className="rs-segmented" role="group" aria-label="Tipologia evento">
              <button
                type="button"
                className={`rs-segmented__item ${eventType === 'qualifying' ? 'is-active' : ''}`}
                onClick={() => setEventType('qualifying')}
              >
                <Icon.Clock size={14} /> Qualifica
              </button>
              <button
                type="button"
                className={`rs-segmented__item ${eventType === 'race' ? 'is-active is-red' : ''}`}
                onClick={() => setEventType('race')}
              >
                <Icon.Flag size={14} /> Gara
              </button>
            </div>
          </div>
          <div className="rs-card__body">
            <p className="setup-mode-desc">
              {eventType === 'qualifying' ? 'Qualifica: classifica per miglior tempo sul giro.' : 'Gara: classifica per posizione in pista.'}
            </p>
            <div className="rs-grid rs-grid--2">
              <label className="rs-field">
                <span className="rs-label">Nome evento</span>
                <input
                  type="text"
                  className="rs-input"
                  placeholder={eventType === 'qualifying' ? 'es. Qualifica Round 3 — Extrema Kart' : 'es. Gara Round 3 — Extrema Kart'}
                  value={eventName}
                  maxLength={120}
                  onChange={(e) => setEventName(e.target.value)}
                />
              </label>
              <label className="rs-field">
                <span className="rs-label">Dettagli<span className="rs-label__opt">opzionale</span></span>
                <textarea
                  className="rs-textarea setup-textarea"
                  rows={2}
                  placeholder="Note, categoria, meteo, regolamento… mostrati nel recap e salvati per l'app"
                  value={eventDetails}
                  maxLength={2000}
                  onChange={(e) => setEventDetails(e.target.value)}
                />
              </label>
            </div>
          </div>
        </section>

        {/* ═══ CIRCUITO ═══ */}
        <section className="rs-card">
          <div className="rs-card__head setup-card-head">
            <span className="rs-card__title"><Icon.Track /> Circuito</span>
            {selectedCircuitName
              ? <span className="rs-badge rs-badge--red"><Icon.Check size={12} /> {selectedCircuitName}</span>
              : <span className="rs-badge">Nessuno selezionato</span>}
          </div>
          <div className="rs-card__body">
            <CircuitPicker
              circuits={circuits}
              value={selectedCircuit}
              onChange={setSelectedCircuit}
              tone="red"
              emptyText="Crea un tracciato dalla sezione Circuiti prima di avviare una gara."
            />
          </div>
        </section>

        {/* ═══ DEVICE GPS ATTIVI ═══ */}
        <section className="rs-card">
          <div className="rs-card__head setup-card-head">
            <span className="rs-card__title rs-card__title--lime"><Icon.Device /> Device GPS attivi</span>
            <span className={`rs-badge ${activeMacs.length ? 'rs-badge--lime' : ''}`}>
              <span className={`rs-badge__dot ${activeMacs.length ? 'rs-badge__dot--live' : ''}`} />
              {activeMacs.length} online
            </span>
          </div>
          <div className="rs-card__body">
            {activeMacs.length === 0 ? (
              <div className="rs-empty">
                <Icon.Device />
                <strong>Nessun device GPS attivo</strong>
                <p>Accendi i dispositivi o avvia il simulatore: compaiono qui appena inviano posizione.</p>
              </div>
            ) : (
              <div className="setup-devices">
                {activeMacs.map(mac => {
                  const assignedId = deviceAssignments[mac] ?? '';
                  const isFlag = assignedId === FLAG_ROLE;
                  // Check if it's a pilot or a team
                  const assignedPilot = pilots.find(p => String(p.id) === assignedId);
                  const assignedTeam = !assignedPilot ? teams.find(t => String(t.id) === assignedId) : null;
                  const assigned = assignedPilot || assignedTeam;
                  const isTeam = !!assignedTeam;

                  const photoUrl = assigned
                    ? isTeam
                      ? (assignedTeam.logoUrl ? `${API_BASE}${assignedTeam.logoUrl}` : TEAM_DEFAULT)
                      : (assignedPilot.photoDriverUrl ? `${API_BASE}${assignedPilot.photoDriverUrl}` : PILOT_DEFAULT)
                    : null;
                  const teamLogoUrl = assignedPilot
                    ? (assignedPilot.photoTeamUrl ? `${API_BASE}${assignedPilot.photoTeamUrl}` : TEAM_DEFAULT)
                    : null;

                  // Se il MAC ha un ID assegnato nel file (device-aliases),
                  // mostriamo l'ID del dispositivo invece del MAC grezzo.
                  const devId = deviceAliases[String(mac).toUpperCase()];
                  const hasDevId = devId != null && String(devId).trim() !== '';
                  const dev = activeDevices[mac] || {};
                  const query = pilotQueryByMac[mac] || '';

                  // scheda ridisegnata solo se cambiano i suoi dati (vedi Memo)
                  return (
                    <Memo key={mac} deps={[activeDevices[mac], deviceAssignments, pilots, teams, deviceAliases, pilotQueryByMac[mac]]} render={() => (
                    <div className={`setup-device ${assignedId ? 'is-assigned' : ''} ${isTeam ? 'is-team' : ''} ${isFlag ? 'is-flag' : ''}`}>
                      <div className="setup-device__head">
                        <span className="setup-device__id" title={mac}>
                          <span className="setup-device__dot" />
                          {hasDevId ? <><span className="setup-device__tag">ID</span>{String(devId).trim()}</> : mac}
                        </span>
                        {isFlag
                          ? <span className="rs-badge rs-badge--warning">Bandiera gialla</span>
                          : assignedId
                            ? <span className={`rs-badge ${isTeam ? 'rs-badge--info' : 'rs-badge--lime'}`}>{isTeam ? 'Team' : 'Pilota'}</span>
                            : <span className="rs-badge rs-badge--outline">Libero</span>}
                      </div>

                      <div className="setup-device__chips">
                        <span className="rs-chip"><Icon.Zap /><strong><LiveDeviceValue store={liveRef} mac={mac} field="speed" format={v => Math.round(v || 0)} /></strong> km/h</span>
                        {dev.fix === false
                          ? <span className="rs-chip setup-chip--nofix" title="Il dispositivo trasmette ma non ha ancora il fix GPS"><Icon.Radio /><strong>GPS</strong> no fix</span>
                          : <span className="rs-chip"><Icon.Radio /><strong>GPS</strong> fix</span>}
                        {dev.hasHr
                          ? <span className="rs-chip setup-chip--hr" title={`Fascia cardio #${dev.beaconId ?? '—'}`}><Icon.Heart /><strong><LiveDeviceValue store={liveRef} mac={mac} field="hr" format={v => v ?? '—'} /></strong> bpm</span>
                          : dev.beaconId
                            ? <span className="rs-chip setup-chip--hr is-idle" title="Fascia agganciata, in attesa del battito"><Icon.Heart />#{dev.beaconId}</span>
                            : null}
                        {hasDevId && <span className="rs-chip setup-device__mac" title={mac}><Icon.Cpu />{mac}</span>}
                      </div>

                      <div className="setup-device__body">
                        {isFlag ? (
                          <div className="setup-assigned setup-flag">
                            <span className="setup-flag__icon"><Icon.Flag size={20} /></span>
                            <span className="setup-assigned__text">
                              <strong>Vettura bandiera gialla</strong>
                              <span>Quando entra in pista la gara passa in YELLOW FLAG da sola; quando esce torna verde.</span>
                            </span>
                            <button
                              type="button"
                              className="rs-btn rs-btn--ghost rs-btn--icon rs-btn--sm"
                              title="Rimuovi ruolo"
                              aria-label="Rimuovi ruolo bandiera gialla"
                              onClick={() => setDeviceAssignments(prev => ({ ...prev, [mac]: '' }))}
                            >
                              <Icon.X size={15} />
                            </button>
                          </div>
                        ) : assigned ? (
                          <div className="setup-assigned">
                            <span className="setup-assigned__photos">
                              <img className="rs-avatar" src={photoUrl} alt={isTeam ? assignedTeam.name : `${assignedPilot.name} ${assignedPilot.surname}`} />
                              {!isTeam && teamLogoUrl && <img className="setup-assigned__team" src={teamLogoUrl} alt={assignedPilot.team || ''} />}
                            </span>
                            <span className="setup-assigned__text">
                              <strong>{isTeam ? assignedTeam.name : `${assignedPilot.name} ${assignedPilot.surname}`}</strong>
                              <span>{isTeam ? (assignedTeam.description || 'Team') : (assignedPilot.team || '—')}</span>
                            </span>
                            <button
                              type="button"
                              className="rs-btn rs-btn--ghost rs-btn--icon rs-btn--sm"
                              title="Rimuovi assegnazione"
                              aria-label="Rimuovi assegnazione"
                              onClick={() => setDeviceAssignments(prev => ({ ...prev, [mac]: '' }))}
                            >
                              <Icon.X size={15} />
                            </button>
                          </div>
                        ) : (
                          <div className="setup-search">
                            <div className="rs-search">
                              <Icon.Search />
                              <input
                                type="text"
                                className="rs-input"
                                placeholder="Cerca pilota o team…"
                                value={query}
                                onChange={(e) => setPilotQueryByMac((s) => ({ ...s, [mac]: e.target.value }))}
                              />
                            </div>

                            {query.trim() !== '' && (
                              (() => {
                                const results = filterPilotsAndTeams(query);
                                return results.length > 0 ? (
                                  <div className="setup-search__results" role="listbox">
                                    {results.map((item) => {
                                      const isItemTeam = item.type === 'team';
                                      const photo = isItemTeam
                                        ? (item.logoUrl ? `${API_BASE}${item.logoUrl}` : TEAM_DEFAULT)
                                        : (item.photoDriverUrl ? `${API_BASE}${item.photoDriverUrl}` : PILOT_DEFAULT);
                                      return (
                                        <button
                                          key={item.id}
                                          type="button"
                                          role="option"
                                          aria-selected={false}
                                          className={`setup-search__item ${isItemTeam ? 'is-team' : ''}`}
                                          onClick={() => selectPilotForMac(mac, item)}
                                        >
                                          <img src={photo} alt="" />
                                          <span className="setup-search__text">
                                            <strong>
                                              {isItemTeam ? item.name : `${item.name} ${item.surname}`}
                                              {isItemTeam && <span className="rs-badge rs-badge--info">Team</span>}
                                            </strong>
                                            <span>{isItemTeam ? (item.description || 'Team') : (item.team || '—')}</span>
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="setup-search__results setup-search__empty">Nessun pilota o team trovato</div>
                                );
                              })()
                            )}

                            {!query.trim() && (
                              <>
                                <p className="setup-device__placeholder"><Icon.Users size={14} /> Cerca e assegna pilota o team</p>
                                <button
                                  type="button"
                                  className="rs-btn rs-btn--sm rs-btn--warning rs-btn--block setup-flag__btn"
                                  title="Usa questo dispositivo come vettura bandiera gialla (safety car)"
                                  onClick={() => setDeviceAssignments(prev => ({ ...prev, [mac]: FLAG_ROLE }))}
                                >
                                  <Icon.Flag size={14} /> Usa come vettura bandiera gialla
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )} />
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ═══ CONFIGURAZIONE GARA (3 card) ═══ */}
        <section className="setup-config" aria-label="Configurazione gara">
          {/* Numero giri — cliccabile per selezionare la modalità */}
          <div
            className={`rs-card setup-mode ${!useTimerMode ? 'rs-card--selected' : ''}`}
            onClick={() => setUseTimerMode(false)}
          >
            <div className="rs-card__head setup-card-head">
              <span className="rs-card__title"><Icon.Layers /> Numero giri</span>
              <div className="setup-mode__tools">
                {!useTimerMode && <span className="rs-badge rs-badge--red"><Icon.Check size={11} /> Attivo</span>}
                <label className="rs-check" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={unlimitedLaps}
                    onChange={(e) => {
                      setUnlimitedLaps(e.target.checked);
                      if (!useTimerMode) return; // già in modalità giri
                      setUseTimerMode(false);
                    }}
                  />
                  Endurance
                </label>
              </div>
            </div>
            <div className="rs-card__body" onClick={(e) => e.stopPropagation()}>
              <div className={`rs-stepper ${unlimitedLaps ? 'is-disabled' : ''}`}>
                <button
                  type="button"
                  className="rs-stepper__btn"
                  aria-label="Meno giri"
                  onClick={() => {
                    setTotalLaps(Math.max(1, totalLaps - 1));
                    setUseTimerMode(false);
                  }}
                  disabled={totalLaps <= 1 || unlimitedLaps}
                >
                  <MinusIcon />
                </button>
                <div className="rs-stepper__value">
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={totalLaps}
                    disabled={unlimitedLaps}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1 && val <= 999) setTotalLaps(val);
                      else if (e.target.value === '') setTotalLaps(1);
                      setUseTimerMode(false);
                    }}
                    onFocus={() => setUseTimerMode(false)}
                  />
                  <span className="rs-stepper__unit">giri</span>
                </div>
                <button
                  type="button"
                  className="rs-stepper__btn"
                  aria-label="Più giri"
                  onClick={() => {
                    setTotalLaps(totalLaps + 1);
                    setUseTimerMode(false);
                  }}
                  disabled={unlimitedLaps}
                >
                  <Icon.Plus size={16} />
                </button>
              </div>
              {!useTimerMode && !unlimitedLaps && <p className="setup-mode__hint is-ok">Gara a {totalLaps} giri</p>}
              {!useTimerMode && unlimitedLaps && <p className="setup-mode__hint is-ok">Modalità Endurance attiva</p>}
              {useTimerMode && <p className="setup-mode__hint">Clicca per selezionare la modalità a giri</p>}
            </div>
          </div>

          {/* Durata gara — cliccabile per selezionare la modalità timer */}
          <div
            className={`rs-card setup-mode ${useTimerMode ? 'rs-card--selected' : ''}`}
            onClick={() => {
              setUseTimerMode(true);
              if (!raceDurationMinutes) setRaceDurationMinutes(60);
            }}
          >
            <div className="rs-card__head setup-card-head">
              <span className="rs-card__title"><Icon.Clock /> Durata gara</span>
              {useTimerMode && <span className="rs-badge rs-badge--red"><Icon.Check size={11} /> Attivo</span>}
            </div>
            <div className="rs-card__body" onClick={(e) => e.stopPropagation()}>
              <div className="rs-stepper">
                <button
                  type="button"
                  className="rs-stepper__btn"
                  aria-label="Meno minuti"
                  onClick={() => {
                    setRaceDurationMinutes(Math.max(5, (raceDurationMinutes || 60) - 5));
                    setUseTimerMode(true);
                  }}
                  disabled={(raceDurationMinutes || 60) <= 5}
                >
                  <MinusIcon />
                </button>
                <div className="rs-stepper__value">
                  <input
                    type="number"
                    min="5"
                    max="999"
                    step="5"
                    value={raceDurationMinutes || 60}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 5 && val <= 999) setRaceDurationMinutes(val);
                      else if (e.target.value === '') setRaceDurationMinutes(5);
                      setUseTimerMode(true);
                    }}
                    onFocus={() => {
                      setUseTimerMode(true);
                      if (!raceDurationMinutes) setRaceDurationMinutes(60);
                    }}
                  />
                  <span className="rs-stepper__unit">min</span>
                </div>
                <button
                  type="button"
                  className="rs-stepper__btn"
                  aria-label="Più minuti"
                  onClick={() => {
                    setRaceDurationMinutes(Math.min(999, (raceDurationMinutes || 60) + 5));
                    setUseTimerMode(true);
                  }}
                  disabled={(raceDurationMinutes || 60) >= 999}
                >
                  <Icon.Plus size={16} />
                </button>
              </div>
              {useTimerMode && <p className="setup-mode__hint is-info">Gara a tempo: {raceDurationMinutes || 60} minuti + Formation Lap</p>}
              {!useTimerMode && <p className="setup-mode__hint">Clicca per selezionare la modalità a tempo</p>}
            </div>
          </div>

          {/* Limite yellow flag */}
          <div className="rs-card setup-mode setup-mode--static">
            <div className="rs-card__head setup-card-head">
              <span className="rs-card__title setup-card-title--yellow"><Icon.Flag /> Limite yellow flag</span>
            </div>
            <div className="rs-card__body">
              <div className="rs-stepper">
                <button
                  type="button"
                  className="rs-stepper__btn"
                  aria-label="Meno km/h"
                  onClick={() => setMaxYellowFlagSpeed(Math.max(10, maxYellowFlagSpeed - 5))}
                  disabled={maxYellowFlagSpeed <= 10}
                >
                  <MinusIcon />
                </button>
                <div className="rs-stepper__value">
                  <input
                    type="number"
                    min="10"
                    max="200"
                    step="5"
                    value={maxYellowFlagSpeed}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 10 && val <= 200) setMaxYellowFlagSpeed(val);
                      else if (e.target.value === '') setMaxYellowFlagSpeed(10);
                    }}
                  />
                  <span className="rs-stepper__unit">km/h</span>
                </div>
                <button
                  type="button"
                  className="rs-stepper__btn"
                  aria-label="Più km/h"
                  onClick={() => setMaxYellowFlagSpeed(Math.min(200, maxYellowFlagSpeed + 5))}
                  disabled={maxYellowFlagSpeed >= 200}
                >
                  <Icon.Plus size={16} />
                </button>
              </div>
              <p className="setup-mode__hint">Violazioni rilevate automaticamente in regime di bandiera gialla</p>
            </div>
          </div>
        </section>

        {/* ═══ START RACE (in fondo, stesso handler dell'header) ═══ */}
        <div className="rs-card setup-footer">
          <div className="setup-footer__text">
            <span className="rs-kicker rs-kicker--red">Pronti al via</span>
            <strong>{startSummary}</strong>
          </div>
          <button type="button" className="rs-btn rs-btn--primary rs-btn--xl" onClick={handleStart}>
            <Icon.Play size={18} /> Start race
          </button>
        </div>
      </div>
    </div>
  );
}
